import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { IoIosCheckmark } from "react-icons/io";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { Select, MenuItem, Tooltip } from "@mui/material";
import { patchRequest } from "../../../components/axiosClient";
import { UPDATE_TASK } from "../../../components/utils";
import { useNavigate } from "react-router-dom";
import high from "../../../components/assets/high_priority.svg";
import low from "../../../components/assets/low_priority.svg";
import normal from "../../../components/assets/normal_priority.svg";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

const TaskInformation = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px;
  min-height: 106px;
  border-radius: 8px;
  max-width: 311px;
  background: white;
  margin-top: 15px;
  gap: 0.75rem;
  box-sizing: border-box !important;

  border-style: solid;
  border-width: 1px;
  border-color: white;

  &:hover {
    border-color: #c9c4c4;
  }
`;

const StyledSelect = styled(Select)`
  &.MuiSelect-root {
    padding: 0;
    color: #7d7d7d;
  }

  .MuiSelect-select {
    padding: 0;
    padding-right: 24px !important;
  }

  &:before,
  &:after {
    display: none;
  }

  &:hover:not(.Mui-disabled):before {
    border-bottom: none;
  }
`;

const PriorityMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    width: 14px;
    height: 14px;
  }
`;

const ProjectLeadCard = ({
  item,
  index,
  onDoubleClick,
  getTaskList,
  handleLeadComplete,
  EmployeeOptions,
}) => {
  const navigate = useNavigate();

  const customIcons = {
    Low: <img src={low} alt="Low" style={{ width: "14px", height: "14px" }} />,
    Medium: (
      <img
        src={normal}
        alt="Medium"
        style={{ width: "14px", height: "14px" }}
      />
    ),
    High: (
      <img src={high} alt="High" style={{ width: "14px", height: "14px" }} />
    ),
  };

  const priorityOptionsList = ["Low", "Medium", "High"];
  const priorityOptions = priorityOptionsList.map((priority) => ({
    label: priority,
    value: priority,
    icon: customIcons[priority],
  }));

  const handleAssigneeChange = async (newValue) => {
    try {
      await patchRequest(
        `${UPDATE_TASK}`,
        {
          assigned_to: newValue,
          task_id: item.id,
        },
        navigate
      );
      getTaskList();
    } catch (error) {
      console.error("Error updating assignee:", error);
    }
  };

  const handlePriorityChange = async (newValue) => {
    try {
      await patchRequest(
        `${UPDATE_TASK}`,
        {
          priority: newValue,
          task_id: item.id,
        },
        navigate
      );
      getTaskList();
    } catch (error) {
      console.error("Error updating priority:", error);
    }
  };

  return (
    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onDoubleClick}
          className="!cursor-pointer border-0 !border-black hover:border-1"
        >
          <TaskInformation
            className={`${
              item?.member_removed === "true"
                ? "!bg-[#fbebeb] border border-[#f1c1c1]"
                : ""
            }  ${item?.is_completed ? "opacity-60" : "opacity-100"} `}
          >
            <div className="flex items-center gap-1 w-full">
              <div
                className={`rounded-full border flex items-center ${
                  !item?.is_completed
                    ? "bg-white text-black"
                    : "bg-green-600 text-white"
                } cursor-pointer`}
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleLeadComplete(item.id, item?.is_completed);
                  await getTaskList();
                }}
              >
                <IoIosCheckmark />
              </div>
              <p className="truncate">{item?.title}</p>
            </div>

            <div className="flex justify-between items-center w-full text-sm text-[#7D7D7D]">
              <div onClick={(e) => e.stopPropagation()}>
                <StyledSelect
                  value={item?.assignee?.id || ""}
                  onChange={(e) => handleAssigneeChange(e.target.value)}
                  displayEmpty
                  variant="standard"
                >
                  {EmployeeOptions.map((member) => (
                    <MenuItem key={member.value} value={member.value}>
                      {member.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </div>
              <span>
                {item?.due_date && dayjs(item?.due_date).format("DD-MM-YYYY")}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm w-full text-[#7D7D7D]">
              <div>
                {item.tags.length > 0 &&
                  item.tags.map((tag, idx) => (
                    <>
                      {idx < 4 && (
                        <Tooltip title={tag}>
                          <LocalOfferIcon
                            sx={{ fontSize: "20px", color: "#6ec38e" }}
                          />
                        </Tooltip>
                      )}
                    </>
                  ))}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <StyledSelect
                  value={item?.priority || ""}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                  displayEmpty
                  variant="standard"
                  renderValue={(selected) => (
                    <div className="flex items-center gap-1">
                      {selected ? (
                        <>
                          {customIcons[selected]}
                          {selected}
                        </>
                      ) : (
                        "--"
                      )}
                    </div>
                  )}
                >
                  {priorityOptions.map((option) => (
                    <PriorityMenuItem key={option.value} value={option.value}>
                      {option.icon}
                      {option.label}
                    </PriorityMenuItem>
                  ))}
                </StyledSelect>
              </div>
            </div>
          </TaskInformation>
        </div>
      )}
    </Draggable>
  );
};

export default ProjectLeadCard;
